import React, { useMemo } from 'react';
import { Building, Clock, DollarSign, CheckCircle, AlertTriangle, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { getAvailableUnitsForDateRange, getRevenueOpportunities } from '../../utils/enhancedAvailabilityUtils';

export const EnhancedUnitGrid = ({
    units,
    selectedUnit,
    checkIn,
    checkOut,
    onUnitSelect,
    excludeBookingId,
    bookings = [],
    isLoading
}) => {
    const { t } = useTranslation();

    // Get availability data when dates are selected
    const availabilityData = useMemo(() => {
        if (!checkIn || !checkOut) return null;

        try {
            return getAvailableUnitsForDateRange(units, checkIn, checkOut, excludeBookingId, bookings);
        } catch (error) {
            console.error('Error getting availability data:', error);
            return null;
        }
    }, [units, checkIn, checkOut, excludeBookingId, bookings]);

    // Get revenue opportunities
    const revenueOpportunities = useMemo(() => {
        if (!checkIn || !checkOut) return [];
        return getRevenueOpportunities(units, checkIn, checkOut, bookings);
    }, [units, checkIn, checkOut, bookings]);

    // Enhanced units with availability info
    const enhancedUnits = useMemo(() => {
        return units.map(unit => {
            let availabilityStatus = 'available';
            let specialTiming = null;
            let revenueOpportunity = null;
            let availabilityDetails = null;

            if (availabilityData) {
                // Check if unit is available for the entire period
                const isFullyAvailable = availabilityData.every(dayAvailability =>
                    dayAvailability.availableUnits.includes(unit.id)
                );

                const isPartiallyAvailable = availabilityData.some(dayAvailability =>
                    dayAvailability.partiallyAvailableUnits.some(partial => partial.unitId === unit.id)
                );

                const isBlocked = availabilityData.some(dayAvailability =>
                    dayAvailability.blockedUnits.includes(unit.id)
                );

                if (isBlocked && !isFullyAvailable && !isPartiallyAvailable) {
                    availabilityStatus = 'blocked';
                } else if (isPartiallyAvailable) {
                    availabilityStatus = 'partial';

                    // Get special timing info
                    const partialInfo = availabilityData
                        .flatMap(day => day.partiallyAvailableUnits)
                        .find(partial => partial.unitId === unit.id);

                    if (partialInfo) {
                        specialTiming = `${t('availableFrom')} ${partialInfo.availableFrom}`;
                        revenueOpportunity = partialInfo.revenueOpportunity || null;
                        availabilityDetails = partialInfo.availabilityType === 'early-checkout'
                            ? t('earlyCheckoutOpportunity')
                            : t('lateCheckoutImpact');
                    }
                }
            }

            // Check for revenue opportunities
            const unitRevenue = revenueOpportunities.find(opp => opp.unitId === unit.id);
            if (unitRevenue && !revenueOpportunity) {
                revenueOpportunity = unitRevenue.potentialRevenue;
            }

            return {
                ...unit,
                availabilityStatus,
                specialTiming,
                revenueOpportunity,
                availabilityDetails,
                available: availabilityStatus !== 'blocked'
            };
        });
    }, [units, availabilityData, revenueOpportunities, t]);

    // Filter units based on availability when dates are selected
    const displayUnits = useMemo(() => {
        if (!checkIn || !checkOut) {
            return enhancedUnits; // Show all units when no dates selected
        }

        // When dates are selected, prioritize available units
        return enhancedUnits.sort((a, b) => {
            // Available units first
            if (a.availabilityStatus === 'available' && b.availabilityStatus !== 'available') return -1;
            if (b.availabilityStatus === 'available' && a.availabilityStatus !== 'available') return 1;

            // Partial availability second
            if (a.availabilityStatus === 'partial' && b.availabilityStatus === 'blocked') return -1;
            if (b.availabilityStatus === 'partial' && a.availabilityStatus === 'blocked') return 1;

            // Revenue opportunities first within same availability status
            if (a.revenueOpportunity && !b.revenueOpportunity) return -1;
            if (b.revenueOpportunity && !a.revenueOpportunity) return 1;

            return 0;
        });
    }, [enhancedUnits, checkIn, checkOut]);

    const getUnitStatusBadge = (unit) => {
        switch (unit.availabilityStatus) {
            case 'available':
                return (
                    <div className="flex items-center space-x-1 rtl:space-x-reverse px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                        <CheckCircle size={12} />
                        <span>{t('available')}</span>
                    </div>
                );
            case 'partial':
                return (
                    <div className="flex items-center space-x-1 rtl:space-x-reverse px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-medium">
                        <Clock size={12} />
                        <span>{t('specialTiming')}</span>
                    </div>
                );
            case 'blocked':
                return (
                    <div className="flex items-center space-x-1 rtl:space-x-reverse px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                        <AlertTriangle size={12} />
                        <span>{t('blocked')}</span>
                    </div>
                );
            default:
                return null;
        }
    };

    const getRevenueOpportunityBadge = (revenueOpportunity) => (
        <div className="flex items-center space-x-1 rtl:space-x-reverse px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
            <DollarSign size={12} />
            <span>+{revenueOpportunity} {t('currency')}</span>
        </div>
    );

    // Show loading state
    if (isLoading && displayUnits.length === 0) {
        return (
            <div className=''>
                <div className="flex items-center justify-center">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className="text-slate-600">{t('loadingUnits')}</p>
                    </div>
                </div>
            </div>
        );
    }


    return (
        <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-100">
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-base sm:text-lg font-semibold text-slate-900 flex items-center space-x-2 rtl:space-x-reverse">
                    <Building size={20} className="text-blue-600" />
                    <span>
                        {checkIn && checkOut
                            ? t('availableUnitsForPeriod')
                            : t('allUnits')
                        }
                    </span>
                </h3>

                {checkIn && checkOut && (
                    <div className="text-xs sm:text-sm text-slate-600">
                        {moment(checkIn).format('L')} - {moment(checkOut).format('L')}
                    </div>
                )}
            </div>

            {/* Revenue Opportunities Alert */}
            {revenueOpportunities.length > 0 && (
                <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-green-50 border border-green-200 rounded-xl">
                    <div className="flex items-start space-x-3 rtl:space-x-reverse">
                        <Zap size={20} className="text-green-600 mt-0.5" />
                        <div>
                            <h4 className="font-semibold text-green-900 mb-2 text-sm sm:text-base">
                                {t('revenueOpportunities')} ({revenueOpportunities.length})
                            </h4>
                            <p className="text-green-700 text-xs sm:text-sm mb-3">
                                {t('revenueOpportunitiesDescription')}
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                                {revenueOpportunities.slice(0, 4).map((opp, index) => (
                                    <div key={index} className="bg-white border border-green-200 rounded-lg p-2 sm:p-3">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <div className="font-medium text-slate-900 text-sm">{opp.unitNumber}</div>
                                                <div className="text-xs sm:text-sm text-slate-600">{opp.description}</div>
                                            </div>
                                            <div className="text-green-600 font-bold text-sm">
                                                +{opp.potentialRevenue} {t('currency')}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Summary Stats */}
            {checkIn && checkOut && availabilityData && (
                <div className="mb-4 sm:mt-6 grid grid-cols-3 lg:grid-cols-3 gap-2 sm:gap-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg sm:rounded-xl p-2 sm:p-4 text-center">
                        <div className="text-lg sm:text-xl lg:text-2xl font-bold text-green-700">
                            {displayUnits?.length}
                            {/* {availabilityData.reduce((sum, day) => sum + day.availableUnits.length, 0)} */}
                        </div>
                        <div className="text-xs sm:text-sm text-green-600">{t('fullyAvailable')}</div>
                    </div>

                    <div className="bg-orange-50 border border-orange-200 rounded-lg sm:rounded-xl p-2 sm:p-4 text-center">
                        <div className="text-lg sm:text-xl lg:text-2xl font-bold text-orange-700">
                            {availabilityData.reduce((sum, day) => sum + day.partiallyAvailableUnits.length, 0)}
                        </div>
                        <div className="text-xs sm:text-sm text-orange-600">{t('partiallyAvailable')}</div>
                    </div>

                    <div className="bg-red-50 border border-red-200 rounded-lg sm:rounded-xl p-2 sm:p-4 text-center">
                        <div className="text-lg sm:text-xl lg:text-2xl font-bold text-red-700">
                            {availabilityData.reduce((sum, day) => sum + day.blockedUnits.length, 0)}
                        </div>
                        <div className="text-xs sm:text-sm text-red-600">{t('blocked')}</div>
                    </div>

                    {/* <div className="bg-blue-50 border border-blue-200 rounded-lg sm:rounded-xl p-2 sm:p-4 text-center col-span-2 lg:col-span-1">
                        <div className="text-lg sm:text-xl lg:text-2xl font-bold text-blue-700">
                            {displayUnits.reduce((sum, unit) => sum + unit.cost, 0)}
                        </div>
                        <div className="text-xs sm:text-sm text-blue-600">{t('totalOpportunity')} {t('currency')}</div>
                    </div> */}
                </div>
            )}

            {/* Units Grid */}
            {displayUnits.length === 0 ? (
                <div className="text-center py-6 sm:py-8">
                    <Building size={32} className="mx-auto text-slate-300 mb-4" />
                    <h3 className="text-base sm:text-lg font-medium text-slate-900 mb-2">
                        {checkIn && checkOut
                            ? t('noUnitsAvailable')
                            : t('noUnits')
                        }
                    </h3>
                    <p className="text-slate-500 text-sm px-4">
                        {checkIn && checkOut
                            ? t('tryDifferentDates')
                            : t('addUnitsFirst')
                        }
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
                    {displayUnits.map(unit => (
                        <div
                            key={unit.id}
                            onClick={() => unit.available && onUnitSelect(unit.id)}
                            className={`relative rounded-xl sm:rounded-2xl border-2 transition-all duration-200 ${selectedUnit === unit.id
                                ? 'border-blue-500 bg-blue-50 shadow-lg scale-[1.02]'
                                : unit.available
                                    ? 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-md cursor-pointer'
                                    : 'border-red-200 bg-red-50 opacity-60 cursor-not-allowed'
                                }`}
                        >
                            {/* Revenue Opportunity Indicator */}
                            {unit.revenueOpportunity && unit.revenueOpportunity > 0 && (
                                <div className="absolute -top-2 -right-2 rtl:-left-2 rtl:right-auto z-10">
                                    <div className="bg-green-500 text-white px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full text-xs font-bold flex items-center space-x-1 rtl:space-x-reverse shadow-lg">
                                        <DollarSign size={10} />
                                        <span>+{unit.revenueOpportunity}</span>
                                    </div>
                                </div>
                            )}

                            {/* Special Timing Indicator */}
                            {unit.specialTiming && (
                                <div className="absolute -top-2 -left-2 rtl:-right-2 rtl:left-auto z-10">
                                    <div className="bg-orange-500 text-white px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full text-xs font-bold flex items-center space-x-1 rtl:space-x-reverse shadow-lg">
                                        <Clock size={10} />
                                        <span className="hidden sm:inline">{t('specialTiming')}</span>
                                        <span className="sm:hidden">⏰</span>
                                    </div>
                                </div>
                            )}

                            <div className="p-3 sm:p-4 lg:p-6">
                                {/* Unit Header */}
                                <div className="flex items-start justify-between mb-3 sm:mb-4">
                                    <div>
                                        <h3 className="text-sm sm:text-base lg:text-lg font-bold text-slate-900">{unit.unitName}</h3>
                                        <p className="text-xs sm:text-sm text-slate-600">{t('unitNumber', { number: unit.unitCode })}</p>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-base sm:text-lg lg:text-xl font-bold text-slate-900">{unit.cost} {t('currency')}</div>
                                        <div className="text-xs text-slate-500">{t('per')} {t(unit.costInterval)}</div>
                                    </div>
                                </div>

                                {/* Unit Details */}
                                <div className="flex items-center space-x-2 sm:space-x-4 rtl:space-x-reverse mb-3 sm:mb-4 text-xs sm:text-sm text-slate-600">
                                    <span>{unit.bedrooms} {t('room')}</span>
                                    <span>{unit.baths} {t('baths')}</span>
                                </div>

                                {/* Availability Status */}
                                <div className="space-y-2 mb-3 sm:mb-4">
                                    {getUnitStatusBadge(unit)}

                                    {unit.specialTiming && (
                                        <div className="text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded-lg p-1.5 sm:p-2">
                                            {unit.specialTiming}
                                        </div>
                                    )}

                                    {unit.availabilityDetails && (
                                        <div className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg p-1.5 sm:p-2">
                                            {unit.availabilityDetails}
                                        </div>
                                    )}
                                </div>

                                {/* Revenue Opportunity Details */}
                                {unit.revenueOpportunity && unit.revenueOpportunity > 0 && (
                                    <div className="bg-green-50 border border-green-200 rounded-lg p-2 sm:p-3">
                                        <div className="flex items-center space-x-2 rtl:space-x-reverse mb-1">
                                            <DollarSign size={12} className="text-green-600" />
                                            <span className="text-xs sm:text-sm font-medium text-green-900">
                                                {t('additionalRevenue')}
                                            </span>
                                        </div>
                                        <div className="text-green-700 text-xs sm:text-sm">
                                            +{unit.revenueOpportunity} {t('currency')} {t('sameDayBooking')}
                                        </div>
                                    </div>
                                )}

                                {/* Amenities */}
                                {/* <div className="flex flex-wrap gap-1 mt-3 sm:mt-4">
                                    {unit.amenities.slice(0, 3).map((amenity, index) => (
                                        <span key={index} className="px-1.5 py-0.5 sm:px-2 sm:py-1 bg-slate-100 text-slate-600 text-xs rounded-full">
                                            {amenity}
                                        </span>
                                    ))}
                                    {unit.amenities.length > 3 && (
                                        <span className="px-1.5 py-0.5 sm:px-2 sm:py-1 bg-slate-100 text-slate-600 text-xs rounded-full">
                                            +{unit.amenities.length - 3}
                                        </span>
                                    )}
                                </div> */}
                            </div>
                        </div>
                    ))}
                </div>
            )}


        </div>
    );
};
